import { useState, useEffect } from "react";
import { axiosInstance } from "../../lib/axios";
import { Plus, Copy } from "lucide-react";
import toast from "react-hot-toast";
import { useClientStore } from "../../store/useClientStore";
import InputField from "../ui/InputField";
import SelectField from "../ui/SelectField";
import Button from "../ui/Button";
import CopyButton from "../ui/CopyButton";

const initialState = {
  name: "",
  tenant: "",
  email: "",
  phone: "",
  website: "",
  company: "",
  location: "",
  timezone: "",
  notes: "",
  leadStatus: "Pending",
  leadSource: "Upwork - Rizwan",
  tier: "Standard",
  contactType: "Individual",
  password: "",
};

interface ClientFormProps {
  onSuccess?: () => void;
  onCredentialsReady?: (name: string, email: string, password: string) => void;
}

const generatePassword = (length = 10) => {
  const chars =
    "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+";
  let password = "";
  for (let i = 0; i < length; i++) {
    password += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return password;
};

const statusOptions = [
  { value: "Pending", label: "Pending" },
  { value: "Need to Contact", label: "Need to Contact" },
  { value: "Contacted - No Response", label: "Contacted - No Response" },
  { value: "Contact in Future", label: "Contact in Future" },
  { value: "Negotiated", label: "Negotiated" },
  { value: "Lost Lead", label: "Lost Lead" },
  { value: "Won Lead", label: "Won Lead" },
];

const sourceOptions = [
  { value: "Upwork - Irfan Baig", label: "Upwork - Irfan Baig" },
  { value: "Upwork - Nadeem", label: "Upwork - Nadeem" },
  { value: "Upwork - Farah", label: "Upwork - Farah" },
  { value: "Upwork - Rizwan", label: "Upwork - Rizwan" },
  { value: "Website - LogoGuts", label: "Website - LogoGuts" },
  { value: "Website - Multiversesol", label: "Website - Multiversesol" },
  { value: "Website - e360Digital", label: "Website - e360Digital" },
  { value: "Other", label: "Other" },
];

const tierOptions = [
  { value: "Premium", label: "Premium" },
  { value: "Standard", label: "Standard" },
  { value: "Basic", label: "Basic" },
];

// Standardized IANA time-zone list (falls back to a small set if the runtime
// doesn't expose Intl.supportedValuesOf).
const timezoneOptions = [
  { value: "", label: "Select time zone (optional)" },
  ...((typeof (Intl as any).supportedValuesOf === "function"
    ? (Intl as any).supportedValuesOf("timeZone")
    : ["UTC", "America/New_York", "America/Los_Angeles", "Europe/London", "Asia/Karachi", "Asia/Dubai"]
  ).map((z: string) => ({ value: z, label: z.replace(/_/g, " ") }))),
];

const contactTypeOptions = [
  { value: "Individual", label: "Individual" },
  { value: "Agency", label: "Agency" },
];

const ClientForm = ({ onSuccess, onCredentialsReady }: ClientFormProps) => {
  const { createClient } = useClientStore();
  const [newClient, setNewClient] = useState(initialState);
  // Brand/Tenant options — configuration-driven from the server, never hardcoded.
  const [tenants, setTenants] = useState<{ _id: string; name: string }[]>([]);
  useEffect(() => {
    axiosInstance.get("/tenants").then((r) => setTenants(r.data?.tenants || [])).catch(() => {});
  }, []);
  // Custom lead source, used only when "Other" is selected.
  const [customSource, setCustomSource] = useState("");
  const [lastAddedClient, setLastAddedClient] = useState<{
    name: string;
    email: string;
    password: string;
  } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newClient.name) return toast.error("Name is required");
    if (!newClient.tenant) return toast.error("Brand / Company is required");

    const payload = { ...newClient };
    // "Other" → store the custom source the BD typed as the actual lead source.
    if (newClient.leadSource === "Other" && customSource.trim()) {
      payload.leadSource = customSource.trim();
    }
    let generatedPassword = "";

    if (newClient.email) {
      generatedPassword = generatePassword();
      payload.password = generatedPassword;
    }

    try {
      await createClient(payload as any);

      if (newClient.email) {
        setLastAddedClient({
          name: newClient.name,
          email: newClient.email,
          password: generatedPassword,
        });
      }

      setNewClient(initialState);

      if (!newClient.email) {
        onSuccess?.();
        setLastAddedClient(null);
      }
    } catch {
      toast.error("Failed to add client");
    }
  };

  const handleCopyAndClose = () => {
    if (!lastAddedClient) return;

    const text = `Name: ${lastAddedClient.name}\nEmail: ${lastAddedClient.email}\nPassword: ${lastAddedClient.password}`;

    navigator.clipboard.writeText(text).then(() => {
      toast.success("Credentials copied to clipboard!");
      onCredentialsReady?.(
        lastAddedClient.name,
        lastAddedClient.email,
        lastAddedClient.password
      );
      setLastAddedClient(null);
    });
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="grid grid-cols-1 md:grid-cols-2 gap-5 p-6 bg-white rounded-2xl shadow-xl border border-gray-200/50 hover:shadow-2xl transition-all duration-300"
    >
      <InputField
        label="Name"
        value={newClient.name}
        onChange={(e) =>
          setNewClient({ ...newClient, name: e.target.value })
        }
        required
        placeholder="Enter client's full name"
      />
      <InputField
        label="Email"
        value={newClient.email}
        onChange={(e) =>
          setNewClient({ ...newClient, email: e.target.value })
        }
        type="email"
        placeholder="Enter client's email address"
      />
      <InputField
        label="Phone"
        value={newClient.phone}
        onChange={(e) =>
          setNewClient({ ...newClient, phone: e.target.value })
        }
        placeholder="Enter contact number (optional)"
      />
      <InputField
        label="Website"
        value={newClient.website}
        onChange={(e) =>
          setNewClient({ ...newClient, website: e.target.value })
        }
        placeholder="Enter website URL (optional)"
      />
      <div className="md:col-span-2">
        <InputField
          label="Company"
          value={newClient.company}
          onChange={(e) =>
            setNewClient({ ...newClient, company: e.target.value })
          }
          placeholder="Enter company name (optional)"
        />
      </div>
      <InputField
        label="Location"
        value={newClient.location}
        onChange={(e) =>
          setNewClient({ ...newClient, location: e.target.value })
        }
        placeholder="City, Country (optional)"
      />
      <SelectField
        label="Time Zone"
        value={newClient.timezone}
        onChange={(e) =>
          setNewClient({ ...newClient, timezone: e.target.value })
        }
        options={timezoneOptions}
      />
      <SelectField
        label="Client Tier"
        value={newClient.tier}
        onChange={(e) =>
          setNewClient({ ...newClient, tier: e.target.value })
        }
        options={tierOptions}
      />
      <SelectField
        label="Contact Type"
        value={newClient.contactType}
        onChange={(e) =>
          setNewClient({ ...newClient, contactType: e.target.value })
        }
        options={contactTypeOptions}
      />
      <div className="md:col-span-2">
        <SelectField
          label="Lead Status"
          value={newClient.leadStatus}
          onChange={(e) =>
            setNewClient({ ...newClient, leadStatus: e.target.value })
          }
          options={statusOptions}

        />
      </div>
      <div className="md:col-span-2">
        <SelectField
          label="Brand / Company"
          required
          value={newClient.tenant}
          onChange={(e) => setNewClient({ ...newClient, tenant: e.target.value })}
          options={[
            { value: "", label: "Select the brand this client belongs to…" },
            ...tenants.map((t) => ({ value: t._id, label: t.name })),
          ]}
        />
      </div>
      <div className="md:col-span-2">
        <SelectField
          label="Lead Source"
          value={newClient.leadSource}
          onChange={(e) =>
            setNewClient({ ...newClient, leadSource: e.target.value })
          }
          options={sourceOptions}

        />
        {newClient.leadSource === "Other" && (
          <div className="mt-2">
            <InputField
              label="Specify Lead Source"
              value={customSource}
              onChange={(e) => setCustomSource(e.target.value)}
              placeholder="Type the custom lead source (e.g. Referral, LinkedIn)"
            />
          </div>
        )}
      </div>
      <div className="md:col-span-2">
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          Notes
        </label>
        <textarea
          className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2462b2]/20 focus:border-[#2462b2] transition-all resize-none h-24"
          value={newClient.notes}
          onChange={(e) =>
            setNewClient({ ...newClient, notes: e.target.value })
          }
          placeholder="Add any important notes or context about this client..."
        />
      </div>

      {lastAddedClient && (
        <div className="md:col-span-2 bg-gradient-to-r from-[#2462b2]/5 to-[#2462b2]/10 border border-[#2462b2]/20 rounded-xl p-5 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 shadow-sm">
          <div>
            <p className="font-semibold text-gray-800 mb-1">
              Generated Credentials
            </p>
            <p className="text-sm text-gray-700">
              <strong>Name:</strong> {lastAddedClient.name}
            </p>
            <p className="text-sm text-gray-700">
              <strong>Email:</strong> {lastAddedClient.email}
            </p>
            <p className="text-sm text-gray-700">
              <strong>Password:</strong> {lastAddedClient.password}
            </p>
          </div>

          <div className="flex gap-2">
            <CopyButton
              text={`Name: ${lastAddedClient.name}\nEmail: ${lastAddedClient.email}\nPassword: ${lastAddedClient.password}`}
              label="Copy"
            />
            <Button
              variant="primary"
              size="sm"
              onClick={handleCopyAndClose}
            >
              <Copy size={14} /> Copy & Close
            </Button>
          </div>
        </div>
      )}

      <Button type="submit" variant="primary" className="md:col-span-2">
        <Plus size={20} /> Add Contact
      </Button>
    </form>
  );
};

export default ClientForm;
